import { Box, Stack, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';

import { EsAccountStatus } from 'common/src/types/enums/EsAccountStatus.enum';
import {
  ES_PASSWORD_RULES,
  ES_USERNAME_LENGTH_CONSTRAINT,
  ES_USERNAME_WARNING,
  isEnergyStarUsernameValid,
} from 'common/src/validators/energyStarCredentialValidation';
import {
  isPasswordValid,
  PASSWORDS_MATCH_CONSTRAINT,
} from 'common/src/validators/passwordValidation';

import { createEnergyStarAccount } from 'portal/src/api/OrganizationsService';
import { FormContainer } from 'portal/src/components/common/forms/FormContainer';
import useFormControlError from 'portal/src/components/common/forms/useFormControlError';
import GenericDialog from 'portal/src/components/common/GenericDialog';
import PasswordRulesDisplay from 'portal/src/components/common/PasswordRulesDisplay';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';
import useSnackbarHandler from 'portal/src/utils/Snackbar/useSnackbarHandler';

import {
  CreateEnergyStarAccountFormValues,
  defaultValues,
  getCreateEnergyStarAccountFields,
} from './CreateEnergyStarAccountForm.fields';

const PASSWORD_RULES = [
  ...ES_PASSWORD_RULES,
  { constraint: PASSWORDS_MATCH_CONSTRAINT, regex: '' },
];

const CreateEnergyStarAccountDialog = ({
  onClose,
  onSuccess,
  open,
  organizationId,
}: {
  onClose: () => void;
  onSuccess: () => void;
  open: boolean;
  organizationId: string;
}) => {
  const snackbarHandler = useSnackbarHandler();

  const {
    control,
    formState: {
      dirtyFields: {
        username: usernameIsDirty,
        password: passwordIsDirty,
        confirmPassword: confirmPasswordIsDirty,
      },
      isDirty,
    },
    handleSubmit,
    reset,
    ...formControl
  } = useForm<CreateEnergyStarAccountFormValues>({
    defaultValues: defaultValues(),
  });
  const { getSnackbarApiErrorMessage } = useFormControlError(formControl);
  const [username, password, confirmPassword] = useWatch({
    control,
    name: ['username', 'password', 'confirmPassword'],
  });
  const usernameIsValid = isEnergyStarUsernameValid(username);
  const { valid: passwordIsValid, results: passwordConstraints } =
    isPasswordValid(password, confirmPassword, PASSWORD_RULES);

  const handleClose = () => {
    reset();
    onClose();
  };

  const { mutate, isPending } = useMutation({
    mutationFn: async ({
      username,
      password,
    }: CreateEnergyStarAccountFormValues) =>
      await createEnergyStarAccount(organizationId, {
        username,
        password,
      }),
    onSuccess: () => {
      snackbarHandler.success(MESSAGES.ORGANIZATION.CREATE_ES_ACCOUNT);
      handleClose();
      onSuccess();
    },
    onError: (err: unknown) => {
      snackbarHandler.error(getSnackbarApiErrorMessage(err));
    },
  });

  return (
    <GenericDialog maxWidth="sm" open={open} onClose={handleClose}>
      <FormContainer
        backButtonlabel="Cancel"
        beforeMainFields={
          <Stack spacing={1} mb={2}>
            <PasswordRulesDisplay
              constraints={[
                {
                  constraint: ES_USERNAME_LENGTH_CONSTRAINT,
                  valid: usernameIsValid,
                },
              ]}
              fieldsAreDirty={usernameIsDirty}
              title="Please ensure your username meets the following requirements:"
              warning={ES_USERNAME_WARNING}
            />
            <PasswordRulesDisplay
              constraints={passwordConstraints}
              fieldsAreDirty={passwordIsDirty || confirmPasswordIsDirty}
            />
          </Stack>
        }
        buttonLayout="bottom"
        columnNumber={1}
        control={control}
        handleBackAction={handleClose}
        handleSubmit={handleSubmit((data) => mutate(data))}
        header={
          <Box mb={2}>
            <Typography variant="h6" gutterBottom>
              Create Portfolio Manager account
            </Typography>
            <Typography variant="body2">
              This will create a new ENERGY STAR Portfolio Manager account for
              you.
            </Typography>
          </Box>
        }
        isDirty={isDirty}
        isNew
        isPending={isPending}
        isSubmitDisabled={!usernameIsValid || !passwordIsValid}
        mainFieldsConfig={getCreateEnergyStarAccountFields()}
        removeContainerStyle
        saveOrUpdateLabel="Create account"
      />
    </GenericDialog>
  );
};

export default CreateEnergyStarAccountDialog;
